import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '../../notification/notification.service';
import {
  ProductionService,
  ProductionQueue,
  ProductionTask,
  CustomProductionJob,
} from '../api';

// ============================================================================
// Gyártás — nyomtatási feladatlista a minimum szint és az aktív rendelések
// alapján. Design: erp-design/screens/17-gyartas.html,
// logika: 23-logic.js productionVals() / logMfg() — a need/target/sorrend
// számítást a backend végzi (GET production/queue), itt csak megjelenítünk.
// ============================================================================

type MfgTone = 'urgent' | 'out' | 'low';

interface MfgRow {
  task: ProductionTask;
  tone: MfgTone;
  badgeLabel: string;
  badgeClass: string;
  meta: string;
  needTime: string;
  pct: number;
  progRed: boolean;
  progLabel: string;
  /** az ebben a munkamenetben erre a tételre rögzített darabszám */
  justLogged: number;
  /** elérte a célt — a sor helyben marad, de már nincs teendő rajta */
  done: boolean;
}

/** Egyedi gyártási sor: egy rendelés egy egyedi tétele. */
interface CustomRow {
  job: CustomProductionJob;
  /** „#rendelésszám · Etsy · Vevő neve" */
  orderLabel: string;
  meta: string;
  needTime: string;
  deadlineLabel: string | null;
  deadlineLate: boolean;
  done: boolean;
}

@Component({
  selector: 'app-gyartas',
  templateUrl: './gyartas.component.html',
  styleUrl: './gyartas.component.scss'
})
export class GyartasComponent implements OnInit, AfterViewInit, OnDestroy {
  loading = true;
  loadFailed = false;
  rows: MfgRow[] = [];
  customRows: CustomRow[] = [];

  totalPieces = 0;
  taskCount = 0;
  hoursLabel = '0 óra';
  gramsLabel = '0 g';

  /** egyedi gyártás összesítő (csak a még el nem készült tételekre) */
  customPieces = 0;
  customOpenCount = 0;
  customHoursLabel = '0 óra';

  /** folyamatban lévő „kész" jelölés tételazonosító szerint */
  customPending: Record<number, boolean> = {};

  /** elkészült db inputok SKU-nként (23-logic.js mfgInputs) */
  inputs: Record<string, string> = {};
  /** folyamatban lévő rögzítés SKU-nként */
  pending: Record<string, boolean> = {};

  /**
   * Azok a SKU-k, amelyek képét már be szabad tölteni (a kártya a nézetbe ért).
   * A képeket az appFetchComponentImage direktíva XHR-rel tölti, ezért ha
   * mind a ~330 feladat képe egyszerre indulna, a backend rate limitere
   * (500 kérés / 10 mp) 429-cel dobná vissza őket — a lista újratöltésekor
   * (pl. gyártás rögzítése után) ez garantáltan bekövetkezett. Csak a látható
   * kártyák képét kérjük le.
   */
  visibleSkus = new Set<string>();

  @ViewChildren('taskEl') taskEls!: QueryList<ElementRef<HTMLElement>>;
  private imageObserver?: IntersectionObserver;

  constructor(
    private production: ProductionService,
    private notification: NotificationService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.observeTasks();
    this.taskEls.changes.subscribe(() => this.observeTasks());
  }

  ngOnDestroy(): void {
    this.imageObserver?.disconnect();
  }

  /** A nézetbe érő feladatkártyák képét engedjük betölteni. */
  private observeTasks(): void {
    if (typeof IntersectionObserver === 'undefined') {
      // Nincs támogatás — töltsön mindent (régi viselkedés).
      this.rows.forEach((row) => this.visibleSkus.add(row.task.sku));
      return;
    }
    this.imageObserver?.disconnect();
    this.imageObserver = new IntersectionObserver(
      (entries) => {
        let changed = false;
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }
          const sku = (entry.target as HTMLElement).dataset['sku'];
          if (sku && !this.visibleSkus.has(sku)) {
            this.visibleSkus.add(sku);
            changed = true;
          }
          this.imageObserver?.unobserve(entry.target);
        }
        if (changed) {
          this.changeDetector.detectChanges();
        }
      },
      { rootMargin: '400px' },
    );
    this.taskEls.forEach((el) => this.imageObserver?.observe(el.nativeElement));
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.production.getQueue().subscribe({
      next: (queue) => {
        this.build(queue);
        this.loading = false;
      },
      error: () => {
        // 404 / hálózati hiba — a production API még nem érhető el
        this.rows = [];
        this.customRows = [];
        this.customPieces = 0;
        this.customOpenCount = 0;
        this.customHoursLabel = '0 óra';
        this.totalPieces = 0;
        this.taskCount = 0;
        this.hoursLabel = '0 óra';
        this.gramsLabel = '0 g';
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  private build(queue: ProductionQueue): void {
    this.buildCustomRows(queue);
    const tasks = (queue?.tasks || []).filter(t => (t.need || 0) > 0);

    // Rendezés a prototípus szerint: sürgős, elfogyott, majd készlet-arány növekvő
    tasks.sort((a, b) =>
      ((b.urgent ? 1 : 0) - (a.urgent ? 1 : 0))
      || ((b.out ? 1 : 0) - (a.out ? 1 : 0))
      || ((a.stock || 0) / Math.max(1, a.target || 0) - (b.stock || 0) / Math.max(1, b.target || 0)));

    const totalPieces = queue?.totalPieces ?? tasks.reduce((s, t) => s + (t.need || 0), 0);
    const totalMinutes = queue?.totalMinutes ?? tasks.reduce((s, t) => s + (t.need || 0) * (t.printMinutes || 0), 0);
    const totalGrams = queue?.totalGrams ?? tasks.reduce((s, t) => s + (t.need || 0) * (t.weightGrams || 0), 0);

    this.totalPieces = totalPieces;
    this.taskCount = tasks.length;
    this.hoursLabel = Math.round(totalMinutes / 60 * 10) / 10 + ' óra';
    this.gramsLabel = totalGrams >= 1000
      ? (Math.round(totalGrams / 100) / 10 + ' kg')
      : (totalGrams + ' g');

    this.rows = tasks.map(t => this.makeRow(t));

    // A listáról lekerült SKU-k beírt értékeit eldobjuk, különben egy később
    // visszatérő tételnél újra felbukkanna a régi, már rögzített darabszám.
    const liveSkus = new Set(this.rows.map(row => row.task.sku));
    Object.keys(this.inputs).forEach(sku => { if (!liveSkus.has(sku)) { delete this.inputs[sku]; } });
    Object.keys(this.pending).forEach(sku => { if (!liveSkus.has(sku)) { delete this.pending[sku]; } });
  }

  /** Egy feladatsor megjelenítési adatai — a betöltés és a helyben frissítés közös forrása. */
  private makeRow(t: ProductionTask, justLogged = 0): MfgRow {
    const stock = t.stock || 0;
    const target = Math.max(1, t.target || 0);
    const tone: MfgTone = t.urgent ? 'urgent' : (t.out ? 'out' : 'low');
    const badge = t.urgent
      ? { label: 'Sürgős · rendelés vár rá', cls: 'stk-pill--red' }
      : (t.out
        ? { label: 'Elfogyott', cls: 'stk-pill--red' }
        : { label: 'Minimum alatt', cls: 'stk-pill--yellow' });
    return {
      task: t,
      tone,
      badgeLabel: badge.label,
      badgeClass: badge.cls,
      meta: t.sku + ' · ' + (t.weightGrams || 0) + ' g · ' + (t.printMinutes || 0) + ' perc / db',
      needTime: '~' + Math.round((t.need || 0) * (t.printMinutes || 0) / 60 * 10) / 10 + ' óra nyomtatás',
      // A készlet negatív is lehet (túlfoglalt komponens). Alsó határ nélkül a
      // width negatív %-ot kapna, amit a böngésző érvénytelenként eldob — így a
      // legrosszabb (pl. -300 db) tételek TELE csíkot mutattak. 0–100 közé zárjuk.
      pct: Math.max(0, Math.min(100, Math.round(stock / target * 100))),
      progRed: !!(t.urgent || t.out),
      progLabel: stock + ' / ' + (t.target || 0) + ' db · min. ' + (t.minStock || 0) + ' db'
        + ((t.reserved || 0) > 0 ? ' + ' + t.reserved + ' db foglalt rendelésekhez' : ''),
      justLogged,
      done: (t.need || 0) <= 0,
    };
  }

  /**
   * A sorok azonosítása SKU szerint. Enélkül az Angular minden újratöltésnél
   * eldobná és újraépítené az összes kártyát, és a beírómezők a DOM-ban más
   * termékhez csúszhatnának át.
   */
  trackBySku = (_index: number, row: MfgRow) => row.task.sku;

  // --------------------------------------------------------------------------
  // Egyedi gyártás — rendelésre készülő tételek
  // --------------------------------------------------------------------------

  private buildCustomRows(queue: ProductionQueue): void {
    const jobs = queue?.customJobs || [];

    this.customRows = jobs.map(job => {
      const deadline = job.deadlineAt ? new Date(job.deadlineAt) : null;
      const deadlineValid = deadline && !Number.isNaN(deadline.getTime());
      return {
        job,
        orderLabel: ['#' + job.orderNumber, job.merchantName, job.customer]
          .filter(Boolean).join(' · '),
        meta: [
          job.sku,
          (job.weightGrams || 0) + ' g',
          (job.printMinutes || 0) + ' perc / db',
        ].join(' · '),
        needTime: '~' + Math.round((job.quantity || 0) * (job.printMinutes || 0) / 60 * 10) / 10
          + ' óra nyomtatás',
        deadlineLabel: deadlineValid ? this.formatDate(deadline!) : null,
        // Lejárt határidő: a mai nap végénél korábbi, és még nincs kész.
        deadlineLate: !!deadlineValid && !job.producedAt && deadline!.getTime() < Date.now(),
        done: !!job.producedAt,
      };
    });

    const open = jobs.filter(job => !job.producedAt);
    this.customOpenCount = open.length;
    this.customPieces = queue?.customPieces
      ?? open.reduce((sum, job) => sum + (job.quantity || 0), 0);
    const customMinutes = queue?.customMinutes
      ?? open.reduce((sum, job) => sum + (job.quantity || 0) * (job.printMinutes || 0), 0);
    this.customHoursLabel = Math.round(customMinutes / 60 * 10) / 10 + ' óra';
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  /** Egyedi tétel „kész" jelölése, illetve a jelölés visszavonása. */
  toggleCustomDone(row: CustomRow): void {
    const id = row.job.orderProductId;
    if (this.customPending[id]) { return; }
    const done = !row.done;
    this.customPending[id] = true;
    this.production.setCustomJobProduced(id, done).subscribe({
      next: (response) => {
        this.customPending[id] = false;
        row.job.producedAt = response?.producedAt ?? (done ? new Date().toISOString() : null);
        this.notification.show(
          done
            ? `${row.job.name} legyártva jelölve (#${row.job.orderNumber}).`
            : `${row.job.name} visszaállítva gyártandóra (#${row.job.orderNumber}).`,
          'success',
        );
        this.load();
      },
      error: () => {
        this.customPending[id] = false;
        this.notification.show('A jelölés mentése nem sikerült.', 'danger');
      },
    });
  }

  /** A rendelés megnyitása az egyedi tételről. */
  openOrder(row: CustomRow): void {
    this.router.navigate(['/orders', row.job.orderId]);
  }

  copyPersonalization(row: CustomRow): void {
    const text = row.job.personalization || '';
    if (!text) { return; }
    navigator.clipboard?.writeText(text).then(
      () => this.notification.show('A personalizációs szöveg a vágólapra másolva.', 'success'),
      () => this.notification.show('A másolás nem sikerült.', 'danger'),
    );
  }

  onQtyInput(sku: string, input: HTMLInputElement): void {
    const digits = input.value.replace(/[^0-9]/g, '');
    input.value = digits;
    this.inputs[sku] = digits;
  }

  qtyOf(sku: string): number {
    return parseInt(this.inputs[sku] || '', 10) || 0;
  }

  canLog(sku: string): boolean {
    return this.qtyOf(sku) > 0 && !this.pending[sku];
  }

  log(row: MfgRow): void {
    const sku = row.task.sku;
    const quantity = this.qtyOf(sku);
    if (quantity <= 0 || this.pending[sku]) { return; }
    this.pending[sku] = true;
    this.production.logProduction({ sku, quantity }).subscribe({
      next: (response) => {
        this.pending[sku] = false;
        this.inputs[sku] = '';
        this.notification.show(
          `${quantity} db ${row.task.name} hozzáadva a készlethez.`,
          'success',
        );
        // FONTOS: rögzítés után NEM töltjük újra a listát. A teljes újratöltés
        // átrendezte és kivette a kész tételeket, így a lenti sorok — és a
        // Rögzítés gombjaik — felcsúsztak a kurzor alá: a következő kattintás
        // már MÁSIK termékre könyvelt. A sort helyben frissítjük, a helyén
        // hagyva; a rendezés csak kézi frissítéskor változik.
        this.applyLoggedQuantity(sku, quantity, response?.newStock);
      },
      error: () => {
        this.pending[sku] = false;
        this.notification.show('Az elkészült darabok rögzítése nem sikerült.', 'danger');
      },
    });
  }

  /** A rögzített darabszám átvezetése a soron — a lista sorrendje nem változik. */
  private applyLoggedQuantity(sku: string, quantity: number, newStock?: number): void {
    const index = this.rows.findIndex(row => row.task.sku === sku);
    if (index < 0) { return; }
    const previous = this.rows[index];
    const stock = Number.isFinite(Number(newStock))
      ? Number(newStock)
      : (previous.task.stock || 0) + quantity;
    const target = previous.task.target || 0;
    const updated: ProductionTask = {
      ...previous.task,
      stock,
      need: Math.max(0, target - stock),
      out: stock <= 0,
      urgent: stock <= 0 && (previous.task.reserved || 0) > 0,
    };
    this.rows[index] = this.makeRow(updated, previous.justLogged + quantity);
    this.recomputeTotals();
  }

  /** Az összesítő kártyák újraszámolása a jelenleg látható sorokból. */
  private recomputeTotals(): void {
    const open = this.rows.filter(row => (row.task.need || 0) > 0);
    this.taskCount = open.length;
    this.totalPieces = open.reduce((sum, row) => sum + (row.task.need || 0), 0);
    const minutes = open.reduce((sum, row) => sum + (row.task.need || 0) * (row.task.printMinutes || 0), 0);
    const grams = Math.round(open.reduce((sum, row) => sum + (row.task.need || 0) * (row.task.weightGrams || 0), 0));
    this.hoursLabel = Math.round(minutes / 60 * 10) / 10 + ' óra';
    this.gramsLabel = grams >= 1000 ? (Math.round(grams / 100) / 10 + ' kg') : (grams + ' g');
  }

  /** Kézi frissítés: ilyenkor rendeződik újra a lista és esnek ki a kész tételek. */
  refresh(): void {
    this.inputs = {};
    this.load();
  }

  /** Van-e olyan sor, ami már elkészült és csak a frissítésre vár. */
  get hasCompletedRows(): boolean {
    return this.rows.some(row => row.done);
  }
}
